import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Fiction from "./pages/Fiction";
import Articles from "./pages/Articles";
import Novels from "./pages/Novels";
import NovelDetail from "./pages/NovelDetail";
import ChapterReader from "./pages/ChapterReader";
import StoryDetail from "./pages/StoryDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import { Toaster } from "./components/ui/toaster";

// Admin imports
import { AdminProvider, useAdmin } from "./contexts/AdminContext";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import StoryEditor from "./pages/admin/StoryEditor";
import AuthorEditor from "./pages/admin/AuthorEditor";
import ContactSubmissions from "./pages/admin/ContactSubmissions";
import AdminNovels from "./pages/admin/AdminNovels";
import NovelEditor from "./pages/admin/NovelEditor";
import ChapterManagement from "./pages/admin/ChapterManagement";
import ChapterEditor from "./pages/admin/ChapterEditor";

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdmin();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
};

function App() {
  return (
    <AdminProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/*"
            element={
              <div className="min-h-screen flex flex-col bg-white">
                <Header />
                <main className="flex-grow">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/fiction" element={<Fiction />} />
                    <Route path="/articles" element={<Articles />} />
                    <Route path="/novels" element={<Novels />} />
                    <Route path="/novel/:id" element={<NovelDetail />} />
                    <Route path="/novel/:novelId/chapter/:chapterId" element={<ChapterReader />} />
                    <Route path="/:category/:id" element={<StoryDetail />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/story/new"
            element={
              <ProtectedRoute>
                <StoryEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/story/edit/:id"
            element={
              <ProtectedRoute>
                <StoryEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/author"
            element={
              <ProtectedRoute>
                <AuthorEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contacts"
            element={
              <ProtectedRoute>
                <ContactSubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/novels"
            element={
              <ProtectedRoute>
                <AdminNovels />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/novel/new"
            element={
              <ProtectedRoute>
                <NovelEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/novel/edit/:id"
            element={
              <ProtectedRoute>
                <NovelEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/novel/chapters/:novelId"
            element={
              <ProtectedRoute>
                <ChapterManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/chapter/new/:novelId"
            element={
              <ProtectedRoute>
                <ChapterEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/chapter/edit/:id"
            element={
              <ProtectedRoute>
                <ChapterEditor />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </AdminProvider>
  );
}

export default App;
